import { useMemo, useState } from 'react';

import ProductCard, { ProductCardSkeleton } from '../components/ProductCard.jsx';
import EmiCalculator from '../components/EmiCalculator.jsx';
import { Badge, Button, EmptyState, ErrorState } from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { cx } from '../lib/format.js';
import { useAsync, useDebounced } from '../lib/useAsync.js';

/**
 * Home — the catalogue.
 *
 * Search is debounced and sent to the API rather than filtered client-side, so
 * behaviour stays correct as the catalogue grows past a single page.
 */

const SORT_OPTIONS = [
  { value: 'emi_asc', label: 'Lowest monthly EMI' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'discount_desc', label: 'Biggest discount' },
  { value: 'name_asc', label: 'Name (A–Z)' },
];

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-ink-200/70 bg-white">
      <div className="bg-grid absolute inset-0 opacity-60" aria-hidden="true" />
      <div
        className="absolute -right-24 -top-32 size-96 rounded-full bg-brand-300/25 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-40 -left-20 size-96 rounded-full bg-growth-300/25 blur-3xl"
        aria-hidden="true"
      />

      <div className="container-page relative py-16 sm:py-20">
        <div className="max-w-3xl">
          <Badge tone="brand">Loan against mutual funds</Badge>

          <h1 className="mt-4 text-4xl font-bold leading-[1.1] tracking-tight text-ink-950 sm:text-5xl lg:text-6xl">
            Buy it now.{' '}
            <span className="bg-gradient-to-r from-brand-600 to-growth-500 bg-clip-text text-transparent">
              Stay invested.
            </span>
          </h1>

          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink-600">
            Pledge your mutual fund units as collateral instead of redeeming them. You get a secured EMI at a rate
            far below a credit card, and your units keep tracking the market for the whole tenure.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button href="#catalogue" size="lg">
              Browse phones
            </Button>
            <Button to="/how-it-works" variant="secondary" size="lg">
              How it works
            </Button>
          </div>

          <dl className="mt-12 grid max-w-2xl grid-cols-2 gap-x-6 gap-y-6 sm:grid-cols-4">
            {[
              { label: 'Interest from', value: '0%' },
              { label: 'Tenures', value: '3–24 mo' },
              { label: 'Equity LTV', value: 'Up to 50%' },
              { label: 'Units redeemed', value: 'None' },
            ].map(({ label, value }) => (
              <div key={label}>
                <dd className="text-2xl font-bold tracking-tight text-ink-950">{value}</dd>
                <dt className="mt-0.5 text-xs font-medium uppercase tracking-wide text-ink-500">{label}</dt>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

export default function Home() {
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('');
  const [sort, setSort] = useState('emi_asc');

  const debouncedSearch = useDebounced(search, 300);

  const { data, error, loading, reload } = useAsync(
    (signal) => api.listProducts({ q: debouncedSearch, brand, sort }, signal),
    [debouncedSearch, brand, sort],
  );

  const products = data?.products ?? [];

  // Brand chips are derived from an unfiltered response so selecting a brand
  // does not remove the other brands from the filter bar.
  const { data: allData } = useAsync((signal) => api.listProducts({}, signal), []);
  const brands = useMemo(() => allData?.meta?.filters?.brands ?? [], [allData]);

  const hasActiveFilters = Boolean(debouncedSearch || brand);

  return (
    <>
      <Hero />

      <section id="catalogue" className="container-page scroll-mt-20 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-ink-950">Available now</h2>
            <p className="mt-1 text-sm text-ink-600">
              Every price below is a real EMI plan stored in the database, not an estimate.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
                <SearchIcon />
              </span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search phones…"
                aria-label="Search products"
                className="h-11 w-56 rounded-xl border border-ink-200 bg-white pl-9 pr-3 text-sm placeholder:text-ink-400 focus:border-brand-400"
              />
            </div>

            <label className="sr-only" htmlFor="sort">
              Sort products
            </label>
            <select
              id="sort"
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              className="h-11 rounded-xl border border-ink-200 bg-white px-3 text-sm font-medium focus:border-brand-400"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {brands.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center gap-2" role="group" aria-label="Filter by brand">
            <button
              type="button"
              onClick={() => setBrand('')}
              aria-pressed={brand === ''}
              className={cx(
                'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
                brand === '' ? 'bg-ink-950 text-white' : 'bg-white text-ink-600 ring-1 ring-ink-200 hover:bg-ink-50',
              )}
            >
              All brands
            </button>
            {brands.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => setBrand(brand === name ? '' : name)}
                aria-pressed={brand === name}
                className={cx(
                  'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
                  brand === name
                    ? 'bg-ink-950 text-white'
                    : 'bg-white text-ink-600 ring-1 ring-ink-200 hover:bg-ink-50',
                )}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        <div className="mt-8">
          {loading && (
            <>
              <p className="sr-only" role="status">
                Loading products
              </p>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {Array.from({ length: 8 }, (_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            </>
          )}

          {!loading && error && (
            <ErrorState
              title="Could not load the catalogue"
              message={error.message}
              onRetry={reload}
            />
          )}

          {!loading && !error && products.length === 0 && (
            <EmptyState
              title={hasActiveFilters ? 'No products match those filters' : 'The catalogue is empty'}
              message={
                hasActiveFilters
                  ? 'Try a different search term or clear the brand filter.'
                  : 'Run the seed script (npm run seed) to load the sample catalogue into Firestore.'
              }
              action={
                hasActiveFilters ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setSearch('');
                      setBrand('');
                    }}
                  >
                    Clear filters
                  </Button>
                ) : null
              }
            />
          )}

          {!loading && !error && products.length > 0 && (
            <>
              <p className="sr-only" role="status">
                {products.length} products found
              </p>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {products.map((product, index) => (
                  <div key={product.id} className="animate-rise" style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}>
                    <ProductCard product={product} priority={index < 4} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <EmiCalculator />
    </>
  );
}
